/**
 * Hook for Refinance Intelligence data operations
 *
 * Provides access to refinance detection, verification, and metrics
 * for tracking loan book conversions from acquired templates to primary.
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../lib/firebase';

export type MatchType = 'exact' | 'normalized' | 'partial' | 'manual';
export type MatchStatus = 'pending' | 'confirmed' | 'rejected';
export type OutcomeType = 'win_back' | 'internal_transfer' | 'cross_template_conversion' | 'office_transfer';
export type RelationshipType = 'refinance' | 'returning_client';

export interface DischargedLoan {
  loanId: string;
  periodMonth: string;
  customerName: string;
  normalizedName: string;
  lender: string | null;
  dischargeDate: string;
  brokerId: string;
  brokerDisplay: string | null;
  officeId: string | null;
  officeName: string | null;
  templateId: string | null;
  templateName: string | null;
  balanceAtDischargeCents: number | null;
}

export interface NewLoan {
  loanId: string;
  periodMonth: string;
  customerName: string;
  normalizedName: string;
  lender: string | null;
  settlementDate: string;
  brokerId: string;
  brokerDisplay: string | null;
  officeId: string | null;
  officeName: string | null;
  templateId: string | null;
  templateName: string | null;
  commissionCents: number;
  loanAmountCents: number;
}

export interface RefinanceMatch {
  matchId: string;
  companyId: string;
  discharged: DischargedLoan;
  newLoan: NewLoan;
  matchType: MatchType;
  matchReason: string;
  confidenceScore: number;
  daysBetween: number;
  isCrossTemplate: boolean;
  isAcquiredToPrimary: boolean;
  isCrossBroker: boolean;
  isCrossOffice: boolean;
  isSameLender: boolean;
  loanValueChangeCents: number | null;
  loanValueChangePercent: number | null;
  status: MatchStatus;
  outcomeType: OutcomeType | null;
  // Relationship classification (client-retention PR-2).
  // relationshipType: AUTHORITATIVE on confirmed rows (reviewer's choice),
  // NULL on legacy confirmed rows (means 'refinance') and on pending rows.
  // suggestedRelationshipType: detector suggestion used to pre-select the
  // relationship radio in the verify UI.
  relationshipType?: RelationshipType | null;
  suggestedRelationshipType?: RelationshipType;
  verifiedByUid: string | null;
  verifiedByEmail: string | null;
  verifiedAt: string | null;
  verificationNotes: string | null;

  // Split loan grouping (computed at query time)
  // When multiple discharged loans are refinanced into one or more new loans,
  // they share the same splitGroupId and should be displayed together
  splitGroupId: string | null;           // NULL = single match, non-null = split loan group
  splitGroupSize: number;                // 1 = single, 2+ = split loan
  splitGroupPosition: number;            // Order within group (1 = primary/largest discharged)
  groupDischargedCount: number;          // Unique discharged loans in group
  groupNewLoanCount: number;             // Unique new loans in group (>1 = split-to-split)
  groupTotalDischargedCents: number;     // Sum of all discharged balances in group
  groupTotalNewLoanCents: number;        // Sum of all new loan amounts in group
  groupPlausibilityPercent: number | null; // (group_total_new_loan / group_total_discharged) * 100
  newLoanFacilityCount: number;          // Number of facilities in new loan (1 = single, 2+ = multiple)
}

export interface YearlyRefinanceData {
  year: number;
  totalMatches: number;
  totalDischarges: number;
  retentionRate: number;
  acquiredToPrimary: number;
  primaryBookRefinances: number;
  totalRecoveredCommissionCents: number;
  totalRecoveredLoanAmountCents: number;
  // Client-retention split (WS3): charted as two series. confirmedRefinances is
  // the refinance-rate numerator; returningClients is tracked separately and is
  // never folded into the rate. Optional: legacy/absent payloads leave undefined.
  confirmedRefinances?: number;
  returningClients?: number;
}

export interface RefinanceMetrics {
  companyId: string;
  periodStart: string;
  periodEnd: string;
  totalMatches: number;
  pendingVerification: number;
  confirmed: number;
  rejected: number;
  winBacks: number;
  internalTransfers: number;
  crossTemplateConversions: number;
  officeTransfers?: number;
  acquiredToPrimary: number;
  // Client-retention split (WS3): returning clients are counted and valued
  // separately from refinances and are never included in the refinance rate
  // or recovered-money totals. Optional: legacy/absent payloads leave undefined.
  returningClients?: number;
  returningClientCommissionCents?: number;
  returningClientLoanAmountCents?: number;
  totalRecoveredCommissionCents: number;
  totalRecoveredLoanAmountCents: number;
  totalDischarges: number;
  retentionRate: number;
  primaryBookRefinances: number;
  winBackRate: number;
  conversionRate: number;
  yearlyBreakdown?: YearlyRefinanceData[];
}

export interface DetectionResult {
  runId: string;
  companyId: string;
  startedAt: string;
  completedAt: string;
  recordsScanned: number;
  matchesFound: number;
  newMatchesInserted: number;
  existingMatchesSkipped: number;
  durationMs: number;
}

export interface UseRefinanceData {
  // Data
  matches: RefinanceMatch[];
  metrics: RefinanceMetrics | null;
  detectionResult: DetectionResult | null;

  // Loading states
  isLoadingMatches: boolean;
  isLoadingMetrics: boolean;
  isRunningDetection: boolean;
  isVerifying: boolean;
  isDeleting: boolean;

  // Errors
  error: Error | null;

  // Pagination
  hasMore: boolean;
  total: number;

  // Actions
  fetchMatches: (companyId: string, filters?: MatchFilters) => Promise<void>;
  fetchMetrics: (companyId: string, filters?: { officeId?: string; brokerId?: string; entityCode?: string }) => Promise<void>;
  // The pairing window is fixed server-side (DISCOVERY_WINDOW, -120..+400 days);
  // the old matchingWindowDays argument was dead (never referenced by the SQL).
  runDetection: (companyId: string) => Promise<DetectionResult>;
  verifyMatch: (matchId: string, companyId: string, status: 'confirmed' | 'rejected' | 'pending', relationshipType: RelationshipType | null, outcomeType?: OutcomeType, notes?: string) => Promise<void>;
  verifyMatchesBatch: (matchIds: string[], companyId: string, status: 'confirmed' | 'rejected' | 'pending', relationshipType: RelationshipType | null, outcomeType?: OutcomeType, notes?: string) => Promise<void>;
  // Backend batch confirm/reject over a HETEROGENEOUS set of decisions (each row
  // carries its own status + relationship + outcome). One callable for the whole
  // backlog clear, vs verifyMatchesBatch which fans out N identical single calls.
  bulkVerifyMatches: (companyId: string, decisions: BulkVerifyDecision[]) => Promise<{ processed: number; autoRejectedTotal: number }>;
  deleteMatch: (matchId: string, companyId: string, reason?: string) => Promise<void>;
  createManualRefinanceMatch: (companyId: string, dischargedLoanId: string, newLoanId: string, notes?: string) => Promise<{ matchId: string; daysBetween: number; suggestedRelationshipType: RelationshipType }>;
  loadMore: () => Promise<void>;
}

/**
 * One decision in a batch-review submission. Mirrors the bulkVerifyRefinanceMatches
 * callable: relationshipType is required by the backend on confirm (default it to
 * the row's suggestedRelationshipType); outcomeType is required only on a
 * refinance confirm.
 */
export interface BulkVerifyDecision {
  matchId: string;
  status: 'confirmed' | 'rejected';
  relationshipType?: RelationshipType | null;
  outcomeType?: OutcomeType;
  notes?: string;
}

export interface MatchFilters {
  status?: MatchStatus | 'all';
  // Optional relationship filter. Confirmed legacy rows have NULL
  // relationship_type, which the backend treats as 'refinance'.
  relationship?: RelationshipType;
  templateId?: string;
  brokerId?: string;
  officeId?: string;
  isAcquiredToPrimary?: boolean;
  sortBy?: 'newest' | 'oldest' | 'confidence_high' | 'confidence_low';
  limit?: number;
  offset?: number;
  entityCode?: string;
}

export function useRefinanceData(): UseRefinanceData {
  const [matches, setMatches] = useState<RefinanceMatch[]>([]);
  const [metrics, setMetrics] = useState<RefinanceMetrics | null>(null);
  const [detectionResult, setDetectionResult] = useState<DetectionResult | null>(null);

  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [isRunningDetection, setIsRunningDetection] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Store current filters for loadMore
  const [currentFilters, setCurrentFilters] = useState<{ companyId: string; filters: MatchFilters } | null>(null);

  // Cache for template names to avoid repeated Firestore lookups
  const [templateNameCache, setTemplateNameCache] = useState<Record<string, string>>({});
  // Cache for office names (keyed by companyId_officeId)
  const [officeNameCache, setOfficeNameCache] = useState<Record<string, string>>({});

  // Fetch template names from Firestore
  const fetchTemplateNames = useCallback(async (templateIds: string[]): Promise<Record<string, string>> => {
    const newIds = templateIds.filter(id => id && !templateNameCache[id]);
    if (newIds.length === 0) return templateNameCache;

    const names: Record<string, string> = { ...templateNameCache };
    await Promise.all(
      newIds.map(async (id) => {
        try {
          const docRef = doc(db, 'fileTemplates', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Field is 'templateName' not 'name'
            names[id] = data.templateName || data.name || 'Unnamed Loan Book';
          }
        } catch (err) {
          console.warn(`[useRefinanceData] Failed to fetch template ${id}:`, err);
        }
      })
    );
    setTemplateNameCache(names);
    return names;
  }, [templateNameCache]);

  // Fetch office names from Firestore
  const fetchOfficeNames = useCallback(async (companyId: string, officeIds: string[]): Promise<Record<string, string>> => {
    const newIds = officeIds.filter(id => id && !officeNameCache[`${companyId}_${id}`]);
    if (newIds.length === 0) {
      // Return existing cache entries for this company
      const result: Record<string, string> = {};
      officeIds.forEach(id => {
        if (id && officeNameCache[`${companyId}_${id}`]) {
          result[id] = officeNameCache[`${companyId}_${id}`];
        }
      });
      return result;
    }

    const names: Record<string, string> = {};
    const cacheUpdates: Record<string, string> = { ...officeNameCache };

    await Promise.all(
      newIds.map(async (id) => {
        try {
          const docRef = doc(db, 'companies', companyId, 'offices', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const name = data.name || 'Unknown Office';
            names[id] = name;
            cacheUpdates[`${companyId}_${id}`] = name;
          }
        } catch (err) {
          console.warn(`[useRefinanceData] Failed to fetch office ${id}:`, err);
        }
      })
    );

    // Also add existing cache entries
    officeIds.forEach(id => {
      if (id && officeNameCache[`${companyId}_${id}`] && !names[id]) {
        names[id] = officeNameCache[`${companyId}_${id}`];
      }
    });

    setOfficeNameCache(cacheUpdates);
    return names;
  }, [officeNameCache]);

  // Enrich matches with template and office names
  const enrichMatches = useCallback(async (matches: RefinanceMatch[], companyId: string): Promise<RefinanceMatch[]> => {
    // Collect all unique template IDs and office IDs
    const templateIds = new Set<string>();
    const officeIds = new Set<string>();

    matches.forEach(m => {
      if (m.discharged.templateId) templateIds.add(m.discharged.templateId);
      if (m.newLoan.templateId) templateIds.add(m.newLoan.templateId);
      if (m.discharged.officeId) officeIds.add(m.discharged.officeId);
      if (m.newLoan.officeId) officeIds.add(m.newLoan.officeId);
    });

    // Fetch names in parallel
    const [templateNames, officeNames] = await Promise.all([
      fetchTemplateNames(Array.from(templateIds)),
      fetchOfficeNames(companyId, Array.from(officeIds)),
    ]);

    // Enrich matches
    return matches.map(m => ({
      ...m,
      discharged: {
        ...m.discharged,
        templateName: m.discharged.templateId ? templateNames[m.discharged.templateId] || null : null,
        officeName: m.discharged.officeId ? officeNames[m.discharged.officeId] || null : null,
      },
      newLoan: {
        ...m.newLoan,
        templateName: m.newLoan.templateId ? templateNames[m.newLoan.templateId] || null : null,
        officeName: m.newLoan.officeId ? officeNames[m.newLoan.officeId] || null : null,
      },
    }));
  }, [fetchTemplateNames, fetchOfficeNames]);

  const fetchMatches = useCallback(async (companyId: string, filters: MatchFilters = {}) => {
    try {
      setIsLoadingMatches(true);
      setError(null);
      setCurrentFilters({ companyId, filters });

      const getMatchesFunc = httpsCallable(functions, 'getRefinanceMatches');
      const response = await getMatchesFunc({
        companyId,
        ...filters,
        offset: filters.offset || 0,
        limit: filters.limit || 100,
      });

      const data = response.data as {
        success: boolean;
        matches: RefinanceMatch[];
        total: number;
        hasMore: boolean;
      };

      // Enrich matches with template and office names from Firestore
      const enrichedMatches = await enrichMatches(data.matches, companyId);

      if (filters.offset && filters.offset > 0) {
        // Append for pagination
        setMatches(prev => [...prev, ...enrichedMatches]);
      } else {
        // Replace for new search
        setMatches(enrichedMatches);
      }
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch (err: any) {
      console.error('[useRefinanceData] fetchMatches error:', err);
      setError(err);
    } finally {
      setIsLoadingMatches(false);
    }
  }, [enrichMatches]);

  const loadMore = useCallback(async () => {
    if (!currentFilters || !hasMore || isLoadingMatches) return;

    const newOffset = matches.length;
    await fetchMatches(currentFilters.companyId, {
      ...currentFilters.filters,
      offset: newOffset,
    });
  }, [currentFilters, hasMore, isLoadingMatches, matches.length, fetchMatches]);

  const fetchMetrics = useCallback(async (companyId: string, filters?: { officeId?: string; brokerId?: string; entityCode?: string }) => {
    try {
      setIsLoadingMetrics(true);
      setError(null);

      const getMetricsFunc = httpsCallable(functions, 'getRefinanceMetrics');
      const response = await getMetricsFunc({ companyId, ...filters });

      const data = response.data as {
        success: boolean;
        metrics: RefinanceMetrics;
      };

      setMetrics(data.metrics);
    } catch (err: any) {
      console.error('[useRefinanceData] fetchMetrics error:', err);
      setError(err);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, []);

  const runDetection = useCallback(async (companyId: string): Promise<DetectionResult> => {
    try {
      setIsRunningDetection(true);
      setError(null);

      const runDetectionFunc = httpsCallable(functions, 'runRefinanceDetection');
      const response = await runDetectionFunc({ companyId });

      const data = response.data as {
        success: boolean;
        result: DetectionResult;
      };

      setDetectionResult(data.result);

      // Refresh matches and metrics after detection
      // Use currentFilters to preserve user's selected filters
      await Promise.all([
        fetchMatches(companyId, currentFilters?.filters || {}),
        fetchMetrics(companyId),
      ]);

      return data.result;
    } catch (err: any) {
      console.error('[useRefinanceData] runDetection error:', err);
      setError(err);
      throw err;
    } finally {
      setIsRunningDetection(false);
    }
  }, [fetchMatches, fetchMetrics, currentFilters]);

  const verifyMatch = useCallback(async (
    matchId: string,
    companyId: string,
    status: 'confirmed' | 'rejected' | 'pending',
    relationshipType: RelationshipType | null,
    outcomeType?: OutcomeType,
    notes?: string
  ): Promise<void> => {
    try {
      setIsVerifying(true);
      setError(null);

      const verifyFunc = httpsCallable(functions, 'verifyRefinanceMatch');
      await verifyFunc({
        matchId,
        companyId,
        status,
        relationshipType,
        outcomeType,
        notes,
      });

      // Update local state
      // relationshipType mirrors the server: authoritative on confirm, cleared
      // on reopen (pending), preserved on reject.
      setMatches(prev =>
        prev.map(m =>
          m.matchId === matchId
            ? status === 'pending'
              ? {
                  // Reopen - clear verification fields
                  ...m,
                  status: 'pending',
                  outcomeType: null,
                  relationshipType: null,
                  verifiedByUid: null,
                  verifiedByEmail: null,
                  verifiedAt: null,
                }
              : {
                  // Confirm/reject - set verification fields
                  ...m,
                  status,
                  outcomeType: outcomeType || null,
                  relationshipType: status === 'confirmed' ? relationshipType : m.relationshipType ?? null,
                  verificationNotes: notes || null,
                  verifiedAt: new Date().toISOString(),
                }
            : m
        )
      );

      // Refresh metrics
      await fetchMetrics(companyId);
    } catch (err: any) {
      console.error('[useRefinanceData] verifyMatch error:', err);
      setError(err);
      throw err;
    } finally {
      setIsVerifying(false);
    }
  }, [fetchMetrics]);

  // Verify every match in a split-loan group in ONE wall-clock round-trip.
  // verifyMatch() refreshes metrics on every call, so verifying an N-portion group
  // sequentially fired N callables + N metrics queries in series (the "confirm lag").
  // This fires all verifyRefinanceMatch callables in parallel, applies a single
  // optimistic state update, and refreshes metrics ONCE for the whole batch.
  const verifyMatchesBatch = useCallback(async (
    matchIds: string[],
    companyId: string,
    status: 'confirmed' | 'rejected' | 'pending',
    relationshipType: RelationshipType | null,
    outcomeType?: OutcomeType,
    notes?: string
  ): Promise<void> => {
    if (matchIds.length === 0) return;
    try {
      setIsVerifying(true);
      setError(null);

      const verifyFunc = httpsCallable(functions, 'verifyRefinanceMatch');
      await Promise.all(
        matchIds.map((matchId) =>
          verifyFunc({ matchId, companyId, status, relationshipType, outcomeType, notes })
        )
      );

      // Single optimistic state update for the whole group
      // relationshipType mirrors the server: authoritative on confirm, cleared
      // on reopen (pending), preserved on reject.
      const idSet = new Set(matchIds);
      setMatches(prev =>
        prev.map(m =>
          idSet.has(m.matchId)
            ? status === 'pending'
              ? {
                  ...m,
                  status: 'pending',
                  outcomeType: null,
                  relationshipType: null,
                  verifiedByUid: null,
                  verifiedByEmail: null,
                  verifiedAt: null,
                }
              : {
                  ...m,
                  status,
                  outcomeType: outcomeType || null,
                  relationshipType: status === 'confirmed' ? relationshipType : m.relationshipType ?? null,
                  verificationNotes: notes || null,
                  verifiedAt: new Date().toISOString(),
                }
            : m
        )
      );

      // Refresh metrics once for the whole batch
      await fetchMetrics(companyId);
    } catch (err: any) {
      console.error('[useRefinanceData] verifyMatchesBatch error:', err);
      setError(err);
      throw err;
    } finally {
      setIsVerifying(false);
    }
  }, [fetchMetrics]);

  // Batch confirm/reject the reviewer's selected pending matches in ONE backend
  // callable (bulkVerifyRefinanceMatches). The board-approved lever for clearing
  // a large pending backlog fast. After the write, refetch the current view so
  // confirmed/rejected rows AND server-side auto-rejected siblings (whose ids the
  // client never knew) both drop out of the pending queue.
  const bulkVerifyMatches = useCallback(async (
    companyId: string,
    decisions: BulkVerifyDecision[]
  ): Promise<{ processed: number; autoRejectedTotal: number }> => {
    if (decisions.length === 0) return { processed: 0, autoRejectedTotal: 0 };
    try {
      setIsVerifying(true);
      setError(null);

      const bulkFunc = httpsCallable(functions, 'bulkVerifyRefinanceMatches');
      const response = await bulkFunc({ companyId, decisions });
      const data = response.data as { processed: number; autoRejectedTotal: number };

      if (currentFilters) {
        await fetchMatches(currentFilters.companyId, currentFilters.filters);
      }
      await fetchMetrics(companyId);

      return data;
    } catch (err: any) {
      console.error('[useRefinanceData] bulkVerifyMatches error:', err);
      setError(err);
      throw err;
    } finally {
      setIsVerifying(false);
    }
  }, [currentFilters, fetchMatches, fetchMetrics]);

  const deleteMatch = useCallback(async (
    matchId: string,
    companyId: string,
    reason?: string
  ): Promise<void> => {
    try {
      setIsDeleting(true);
      setError(null);

      const deleteFunc = httpsCallable(functions, 'deleteRefinanceMatch');
      await deleteFunc({
        matchId,
        companyId,
        reason,
      });

      // Remove from local state
      setMatches(prev => prev.filter(m => m.matchId !== matchId));
      setTotal(prev => Math.max(0, prev - 1));

      // Refresh metrics
      await fetchMetrics(companyId);
    } catch (err: any) {
      console.error('[useRefinanceData] deleteMatch error:', err);
      setError(err);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [fetchMetrics]);

  // Manually link a discharged loan to a successor the matcher cannot see
  // (e.g. surname changes). Lands a PENDING row (match_type='manual') that the
  // reviewer then confirms with a relationship + outcome. Admin roles only;
  // founder is blocked server-side (blockFounderClientWrite).
  const createManualRefinanceMatch = useCallback(async (
    companyId: string,
    dischargedLoanId: string,
    newLoanId: string,
    notes?: string
  ): Promise<{ matchId: string; daysBetween: number; suggestedRelationshipType: RelationshipType }> => {
    try {
      setError(null);

      const createFunc = httpsCallable(functions, 'createManualRefinanceMatch');
      const response = await createFunc({
        companyId,
        dischargedLoanId,
        newLoanId,
        notes,
      });

      const data = response.data as {
        success: boolean;
        matchId: string;
        daysBetween: number;
        suggestedRelationshipType: RelationshipType;
      };

      // Refresh: the new match appears as a pending row under the current view.
      if (currentFilters) {
        await fetchMatches(currentFilters.companyId, currentFilters.filters);
      }
      await fetchMetrics(companyId);

      return {
        matchId: data.matchId,
        daysBetween: data.daysBetween,
        suggestedRelationshipType: data.suggestedRelationshipType,
      };
    } catch (err: any) {
      console.error('[useRefinanceData] createManualRefinanceMatch error:', err);
      setError(err);
      throw err;
    }
  }, [currentFilters, fetchMatches, fetchMetrics]);

  return {
    matches,
    metrics,
    detectionResult,
    isLoadingMatches,
    isLoadingMetrics,
    isRunningDetection,
    isVerifying,
    isDeleting,
    error,
    hasMore,
    total,
    fetchMatches,
    fetchMetrics,
    runDetection,
    verifyMatch,
    verifyMatchesBatch,
    bulkVerifyMatches,
    deleteMatch,
    createManualRefinanceMatch,
    loadMore,
  };
}
